import { formatIDR } from "@marketplace/shared/utils";
import { Table, Text } from "@marketplace/ui";
import type { TFunction } from "i18next";
import { Fragment, useState } from "react";
import { MdKeyboardArrowRight } from "react-icons/md";

import type { OrderDetailItem } from "../../types";
import classes from "./order-detail-modal.module.css";

type OrderItemsTableProps = {
  orderItems: OrderDetailItem[];
  t: TFunction<"order", "orderListPage">;
};

const MobileViewItem: React.FC<{
  label: string;
  value?: string | number;
}> = ({ label, value }) => (
  <div className="flex flex-col gap-2">
    <Text
      type="heading"
      size="ss"
      weight="bold"
      className="text-fjord-grey-500"
    >
      {label}
    </Text>
    <Text
      title={String(value)}
      className="text-fjord-grey-400 truncate"
      size="s"
    >
      {value || "-"}
    </Text>
  </div>
);

const getProductSnapshotData = (orderItem: OrderDetailItem) => {
  return {
    productName: orderItem.product_name,
    package: orderItem.package_name,
    variant: orderItem.variant_name,
    price: orderItem.variant_price
  };
};

const OrderDetailDescriptionItem = ({
  label,
  value
}: {
  label: string;
  value?: string;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <Text size="s" className="text-fjord-grey-500">
        {label}
      </Text>
      <Text
        title={String(value)}
        weight="bold"
        size="s"
        className="text-fjord-grey-400 truncate"
      >
        {value || "-"}
      </Text>
    </div>
  );
};

const ExpandCollapseButton = ({
  isExpanded,
  hasExpandableContent
}: {
  isExpanded: boolean;
  hasExpandableContent: boolean;
}) => {
  if (!hasExpandableContent) {
    return <div className="w-[14px] h-[14px]" />;
  }

  return (
    <div className="flex items-center justify-center w-[14px] h-[14px]">
      <MdKeyboardArrowRight
        className="text-fjord-grey-400 transition-transform duration-300"
        style={{
          transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)"
        }}
        size={14}
      />
    </div>
  );
};

const ExpandableContent = ({
  isExpanded,
  title,
  children
}: {
  isExpanded: boolean;
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isExpanded
          ? "max-h-96 opacity-100 pb-1"
          : "max-h-0 opacity-0 py-0 overflow-hidden"
      }`}
    >
      <div className="bg-[#F9FAFB] p-1 text-left">
        <Text size="s" className="text-fjord-grey-400 mb-2">
          {title}:
        </Text>
        {children}
      </div>
    </div>
  );
};

const ProductRequirementsContent = ({
  requirements
}: {
  requirements: Array<{ title: string; value: string }>;
}) => {
  return (
    <div className="grid grid-cols-2 gap-[14px]">
      {requirements.map((requirement) => (
        <OrderDetailDescriptionItem
          key={requirement.title}
          label={requirement.title}
          value={requirement.value}
        />
      ))}
    </div>
  );
};

const MobileOrderItem = ({
  item,
  isExpanded,
  onToggleExpansion,
  t
}: {
  item: OrderDetailItem;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  t: TFunction<"order", "orderListPage">;
}) => {
  const itemDetail = getProductSnapshotData(item);
  const hasRequirements = Boolean(
    item.order_requirements && item.order_requirements.length > 0
  );

  return (
    <Fragment>
      <div
        className={`flex gap-4 ${hasRequirements ? "cursor-pointer" : ""}`}
        onClick={hasRequirements ? onToggleExpansion : undefined}
      >
        <div className="flex-shrink-0">
          <ExpandCollapseButton
            hasExpandableContent={hasRequirements}
            isExpanded={isExpanded}
          />
        </div>

        <div className="flex-1 grid grid-cols-2 gap-x-2 gap-y-[14px]">
          <MobileViewItem
            label={t("orderDetailModal.table.columns.productName")}
            value={itemDetail.productName}
          />
          <MobileViewItem
            label={t("orderDetailModal.table.columns.package")}
            value={itemDetail.package}
          />
          <MobileViewItem
            label={t("orderDetailModal.table.columns.variant")}
            value={itemDetail.variant}
          />
          <MobileViewItem
            label={t("orderDetailModal.table.columns.price")}
            value={formatIDR(itemDetail.price ?? 0)}
          />
        </div>
      </div>

      {hasRequirements && (
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isExpanded ? "max-h-96 opacity-100 mt-4" : "max-h-0 opacity-0 mt-0"
          }`}
        >
          <div className="bg-[#F9FAFB] p-1 pl-[30px]">
            <Text size="s" className="text-fjord-grey-400 mb-3 font-medium">
              {t("orderDetailModal.productRequirements")}:
            </Text>
            <div className="grid grid-cols-1 gap-3">
              {item.order_requirements?.map((requirement) => (
                <OrderDetailDescriptionItem
                  key={requirement.title}
                  label={requirement.title}
                  value={requirement.value}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

const OrderItemRow = ({
  item,
  hasProductRequirements,
  isExpanded,
  onToggleExpansion,
  t
}: {
  item: OrderDetailItem;
  hasProductRequirements: boolean;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  t: TFunction<"order", "orderListPage">;
}) => {
  const itemDetail = getProductSnapshotData(item);
  const hasRequirements = Boolean(
    item.order_requirements && item.order_requirements.length > 0
  );

  return (
    <Fragment>
      <Table.Tr
        className={`${hasRequirements ? "cursor-pointer" : ""}`}
        onClick={hasRequirements ? onToggleExpansion : undefined}
      >
        {hasProductRequirements && (
          <Table.Td>
            <ExpandCollapseButton
              isExpanded={isExpanded}
              hasExpandableContent={hasRequirements}
            />
          </Table.Td>
        )}
        <Table.Td>{itemDetail.productName}</Table.Td>
        <Table.Td>{itemDetail.package}</Table.Td>
        <Table.Td>{itemDetail.variant}</Table.Td>
        <Table.Td>{formatIDR(itemDetail.price ?? 0)}</Table.Td>
      </Table.Tr>

      {hasRequirements && (
        <Table.Tr className={isExpanded ? "" : "!h-0 overflow-hidden"}>
          <Table.Td className={isExpanded ? "" : "!p-0"} />
          <Table.Td colSpan={4} className={isExpanded ? "" : "!p-0"}>
            <ExpandableContent
              isExpanded={isExpanded}
              title={t("orderDetailModal.productRequirements")}
            >
              <ProductRequirementsContent
                requirements={item.order_requirements!}
              />
            </ExpandableContent>
          </Table.Td>
        </Table.Tr>
      )}
    </Fragment>
  );
};

export const OrderItemsTable = ({ orderItems, t }: OrderItemsTableProps) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => {
    const initialExpandedRows = new Set<number>();
    orderItems.forEach((item) => {
      const hasExpandableContent =
        item.order_requirements && item.order_requirements.length > 0;

      if (hasExpandableContent) {
        initialExpandedRows.add(item.id);
      }
    });
    return initialExpandedRows;
  });

  const hasProductRequirements = orderItems.some(
    (item) => item.order_requirements && item.order_requirements.length > 0
  );

  const toggleRowExpansion = (itemId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  return (
    <>
      <div className="hidden md:block">
        <Table
          withRowBorders={false}
          className="mb-[42px] "
          classNames={classes}
        >
          <Table.Thead>
            <Table.Tr>
              {hasProductRequirements && <Table.Th />}
              <Table.Th>
                {t("orderDetailModal.table.columns.productName")}
              </Table.Th>
              <Table.Th>{t("orderDetailModal.table.columns.package")}</Table.Th>
              <Table.Th>{t("orderDetailModal.table.columns.variant")}</Table.Th>
              <Table.Th>{t("orderDetailModal.table.columns.price")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {orderItems?.map((item) => (
              <OrderItemRow
                key={item.id}
                item={item}
                hasProductRequirements={hasProductRequirements}
                isExpanded={expandedRows.has(item.id)}
                onToggleExpansion={() => toggleRowExpansion(item.id)}
                t={t}
              />
            ))}
          </Table.Tbody>
        </Table>
      </div>

      <div className="md:hidden mb-4 space-y-4">
        {orderItems.map((item) => (
          <Fragment key={item.id}>
            <MobileOrderItem
              item={item}
              isExpanded={expandedRows.has(item.id)}
              onToggleExpansion={() => toggleRowExpansion(item.id)}
              t={t}
            />
            <hr className="mt-4 bg-mischka-100 h-px border-0 w-full" />
          </Fragment>
        ))}
      </div>
    </>
  );
};
